/**
 * @file Route-level Shared Calendar surface owner.
 */

import { appData, state } from "../../app/state/state.js";
import { canAccessView } from "../../app/routing/boardAccess.js";
import { navigate } from "../../app/routing/navigationController.js";
import { getCalendarDayDateKey } from "../../app/routing/calendarRoutes.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { formatDate } from "../../shared/utils/dateUtils.js";
import { isColorDark } from "../../shared/utils/displayUtils.js";
import {
    attachCalendarRouteListeners,
    detachCalendarRouteListeners,
    moveCalendarVisibleMonth,
    showCalendarMonth,
    showCurrentCalendarMonth,
} from "./calendarDataListeners.js";
import {
    addCalendarMonths,
    buildCalendarMonthDays,
    entryOccursOnDate,
    getCalendarDateKey,
    formatCalendarMonthLabel,
} from "./calendarDateUtils.js";
import { openEventDetailModal } from "./eventDetailModal.js";
import { openLeaveDetailModal } from "./leaveDetailModal.js";
import { getCalendarState } from "./calendarState.js";
import { openCalendarAddEntryModal } from "./calendarAddEntryModal.js";
import {
    formatCalendarEntryTokenLabel,
    formatCalendarEntryTooltip,
} from "./calendarDisplayFormatting.js";
import { buildCalendarEntryLayout } from "./calendarEntryLayout.js";
import { openReminderDetailModal } from "./reminderDetailModal.js";

const WEEKDAY_LABELS = Object.freeze(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]);
const MAX_VISIBLE_DAY_ENTRIES = 3;
const MOBILE_TIMELINE_HOUR_HEIGHT = 48;
const MOBILE_TIMELINE_TOP_OFFSET = 0;
const MOBILE_DATE_STRIP_WINDOW_DAYS = 35;
const MOBILE_DATE_STRIP_EDGE_THRESHOLD = 160;

let pendingNavigationDirection = "";
let lastRenderedStartMonth = null;

function getSafeHexColor(value, fallback) {
    return /^#[0-9a-f]{6}$/i.test(String(value || "")) ? value : fallback;
}

function getCalendarColors() {
    const calendarConfig = appData.config?.calendar || {};
    return {
        event: getSafeHexColor(calendarConfig.eventColor, "#DBEAFE"),
        reminder: getSafeHexColor(calendarConfig.reminderColor, "#F78183"),
        leave: getSafeHexColor(calendarConfig.leaveColor, "#DCFCE7"),
        providerLeave: getSafeHexColor(
            calendarConfig.providerLeaveColor,
            "#BBF7D0",
        ),
        nonBusinessDay: getSafeHexColor(
            calendarConfig.nonBusinessDayColor,
            "#F3F4F6",
        ),
        holiday: getSafeHexColor(calendarConfig.holidayColor, "#FEF3C7"),
    };
}

function getContrastTextColor(backgroundColor) {
    return isColorDark(backgroundColor) ? "#FFFFFF" : "#111827";
}

function darkenHexColor(value, amount = 0.28) {
    const hex = getSafeHexColor(value, "#93C5FD").slice(1);
    const channels = [0, 2, 4].map((index) => {
        const channel = parseInt(hex.slice(index, index + 2), 16);
        return Math.max(0, Math.round(channel * (1 - amount)));
    });
    return `#${channels
        .map((channel) => channel.toString(16).padStart(2, "0"))
        .join("")}`;
}

function getCurrentDateKey() {
    return getCalendarDateKey(new Date());
}

function getCurrentMonthKey() {
    return getCurrentDateKey().slice(0, 7);
}

function parseDateKey(dateKey) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ""));
    if (!match) {return null;}
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return getCalendarDateKey(date) === dateKey ? date : null;
}

function addCalendarDays(dateKey, offset) {
    const date = parseDateKey(dateKey);
    if (!date) {return "";}
    date.setDate(date.getDate() + offset);
    return getCalendarDateKey(date);
}

function getCalendarWeekStart(dateKey) {
    const date = parseDateKey(dateKey);
    if (!date) {return getCurrentDateKey();}
    const mondayOffset = (date.getDay() + 6) % 7;
    date.setDate(date.getDate() - mondayOffset);
    return getCalendarDateKey(date);
}

function formatWeekdayLabel(dateKey) {
    const date = parseDateKey(dateKey);
    if (!date) {return "";}
    return new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(date);
}

function formatMonthAbbreviation(dateKey) {
    const date = parseDateKey(dateKey);
    if (!date) {return "";}
    return new Intl.DateTimeFormat(undefined, { month: "short" }).format(date);
}

function getMonthBandClass(dateKey) {
    const date = parseDateKey(dateKey);
    if (!date) {return "calendar-mobile-week-day--month-even";}
    return date.getMonth() % 2 === 0 ?
        "calendar-mobile-week-day--month-even" :
        "calendar-mobile-week-day--month-odd";
}

function getTodayNavigationTarget(calendarRouteState) {
    const currentMonth = getCurrentMonthKey();
    const startMonth = calendarRouteState.visibleStartMonth;
    const endMonth = calendarRouteState.visibleEndMonth || startMonth;
    if (!startMonth) {return "";}
    if (currentMonth < startMonth) {return "previous";}
    if (currentMonth > endMonth) {return "next";}
    return "";
}

function getBusinessCalendarMeta() {
    const businessCalendar = appData.config?.businessCalendar || {};
    const activeDays = Array.isArray(businessCalendar.activeDays) ?
        businessCalendar.activeDays :
        [1, 2, 3, 4, 5];
    const holidays = Array.isArray(businessCalendar.holidays) ?
        businessCalendar.holidays :
        [];
    return {
        activeDays: new Set(activeDays),
        holidaysByDate: new Map(holidays
            .filter((holiday) => holiday?.date)
            .map((holiday) => [holiday.date, holiday])),
    };
}

function getEntriesForDate(entries, dateKey) {
    return entries.filter((entry) => entryOccursOnDate(entry, dateKey));
}

function getEntryTypeRank(entry = {}) {
    const type = entry.sourceType || entry.renderKind || "";
    if (type === "leave") {return 0;}
    if (type === "event") {return 1;}
    if (type === "reminder") {return 2;}
    return 3;
}

function isMultiDayEntry(entry = {}) {
    return Boolean(
        entry.startDate &&
        (entry.endDate || entry.startDate) !== entry.startDate,
    );
}

function getAgendaGroupRank(entry = {}) {
    return isAllDayAgendaEntry(entry) ? 0 : 1;
}

function compareAgendaEntries(a, b) {
    const groupDiff = getAgendaGroupRank(a) - getAgendaGroupRank(b);
    if (groupDiff !== 0) {return groupDiff;}
    if (!isAllDayAgendaEntry(a) && !isAllDayAgendaEntry(b)) {
        const timeDiff = getEntryStartMinute(a) - getEntryStartMinute(b);
        if (timeDiff !== 0) {return timeDiff;}
    }
    const typeDiff = getEntryTypeRank(a) - getEntryTypeRank(b);
    if (typeDiff !== 0) {return typeDiff;}
    return String(a.title || "").localeCompare(String(b.title || ""));
}

function sortAgendaEntries(entries = []) {
    return [...entries].sort(compareAgendaEntries);
}

function getEntryColor(entry, colors) {
    if (entry.renderKind === "leave") {
        return entry.requesterIsAppProvider === true ?
            colors.providerLeave :
            colors.leave;
    }
    if (entry.renderKind === "reminder" || entry.sourceType === "reminder") {
        return colors.reminder;
    }
    return colors.event;
}

function isEntrySegmentStart(entry, day) {
    return entry.startDate === day.dateKey || day.weekday === 1;
}

function isEntrySegmentEnd(entry, day) {
    const endDate = entry.endDate || entry.startDate;
    return endDate === day.dateKey || day.weekday === 0;
}

function shouldShowEntryTitle(entry, day) {
    return entry.startDate === day.dateKey || day.weekday === 1;
}

function getEntrySegmentClasses(entry, day) {
    const isSegmentStart = isEntrySegmentStart(entry, day);
    const isSegmentEnd = isEntrySegmentEnd(entry, day);
    const isMultiDay = entry.startDate && (entry.endDate || entry.startDate) !== entry.startDate;
    if (!isMultiDay) {
        return "w-full rounded";
    }
    const widthClass = !isSegmentStart && !isSegmentEnd ?
        "calendar-entry-segment-middle" :
        "calendar-entry-segment-edge";
    return [
        widthClass,
        isSegmentStart ? "rounded-l" : "rounded-l-none",
        isSegmentEnd ? "rounded-r" : "rounded-r-none",
        isSegmentStart ? "ml-0" : "calendar-entry-segment-offset",
        "z-[1]",
    ].join(" ");
}

function shouldRenderEntryEdge(entry, day) {
    const isFirstDate = entry.startDate === day.dateKey;
    const isAllDayEvent = entry?.sourceType === "event" && entry.isFullDay === true;
    const isLeave = entry?.sourceType === "leave" || entry?.renderKind === "leave";
    return isFirstDate && (isAllDayEvent || isLeave);
}

function getEntryInsetClasses(entry, day) {
    return shouldRenderEntryEdge(entry, day) ? "pl-2.5 pr-1.5" : "px-1.5";
}

function getEntryEdgeHtml(entry, day, entryColor) {
    if (!shouldRenderEntryEdge(entry, day)) {return "";}
    return `
        <span
            class="pointer-events-none absolute inset-y-0 left-0 w-1"
            style="background-color: ${darkenHexColor(entryColor)};"
            aria-hidden="true"
        ></span>
    `;
}

function shouldRenderSegmentAsBar(entry) {
    return (entry.sourceType === "event" || entry.renderKind === "event") ||
        (entry.sourceType === "leave" || entry.renderKind === "leave");
}

function getEntryBarClasses(entry, day) {
    return shouldRenderSegmentAsBar(entry) ?
        getEntrySegmentClasses(entry, day) :
        "rounded";
}

function shouldShowSegmentTitle(entry, day) {
    return shouldRenderSegmentAsBar(entry) ?
        shouldShowEntryTitle(entry, day) :
        entry.startDate === day.dateKey;
}

function renderDayEntries(slots, colors, day) {
    const adjacentMonthClass = day.isCurrentMonth ?
        "" :
        "calendar-entry-token--adjacent-month";
    return slots.map((slot) => {
        if (!slot?.entry) {
            return `<div class="h-[18px]" aria-hidden="true"></div>`;
        }
        const entry = slot.entry;
        const entryColor = getEntryColor(entry, colors);
        const tooltip = formatCalendarEntryTooltip(entry);
        if (entry.renderKind === "reminder" || entry.sourceType === "reminder") {
            return `
                <button
                    type="button"
                    class="calendar-entry-token ${adjacentMonthClass} flex h-[18px] w-full items-center gap-1.5 rounded px-1.5 text-left text-[11px] font-medium text-gray-900 transition-colors hover:bg-white/60"
                    data-calendar-source-type="${escapeHTML(entry.sourceType || "")}"
                    data-calendar-source-id="${escapeHTML(entry.sourceId || "")}"
                    title="${escapeHTML(tooltip)}"
                >
                    <span
                        class="h-2.5 w-2.5 shrink-0 rounded-full border border-black/5"
                        style="background-color: ${entryColor};"
                        aria-hidden="true"
                    ></span>
                    <span class="min-w-0 truncate">${escapeHTML(entry.title || "Untitled reminder")}</span>
                </button>
            `;
        }
        const segmentClasses = getEntryBarClasses(entry, day);
        const title = shouldShowSegmentTitle(entry, day) ?
            formatCalendarEntryTokenLabel(entry) :
            "";
        const edgeHtml = getEntryEdgeHtml(entry, day, entryColor);
        return `
            <button
                type="button"
                class="calendar-entry-token ${adjacentMonthClass} relative h-[18px] overflow-hidden truncate text-left text-[11px] font-medium ${getEntryInsetClasses(entry, day)} ${segmentClasses}"
                data-calendar-source-type="${escapeHTML(entry.sourceType || "")}"
                data-calendar-source-id="${escapeHTML(entry.sourceId || "")}"
                style="background-color: ${entryColor}; color: ${getContrastTextColor(entryColor)};"
                title="${escapeHTML(tooltip)}"
            >
                ${edgeHtml}
                ${escapeHTML(title)}
            </button>
        `;
    }).join("");
}

function renderDayOverflowButton(day, hiddenCount) {
    if (hiddenCount <= 0) {return "";}
    return `
        <button
            type="button"
            class="calendar-day-overflow h-[15px] w-full rounded px-1 py-0 text-left text-[10px] font-semibold leading-[13px] text-gray-600 transition-colors hover:bg-white/70 hover:text-gray-900"
            data-calendar-day-agenda="${escapeHTML(day.dateKey)}"
        >
            +${hiddenCount} more
        </button>
    `;
}

function renderMonthGrid(monthKey, calendarRouteState, options = {}) {
    const colors = getCalendarColors();
    const { activeDays, holidaysByDate } = getBusinessCalendarMeta();
    const days = buildCalendarMonthDays(monthKey);
    const entryLayout = buildCalendarEntryLayout(
        days,
        calendarRouteState.entries,
        MAX_VISIBLE_DAY_ENTRIES,
    );
    const todayDateKey = getCurrentDateKey();
    const isSecondary = options.secondary === true;

    return `
        <section
            class="${isSecondary ? "hidden xl:block" : ""}"
            data-calendar-month="${escapeHTML(monthKey)}"
        >
            <h3 class="mb-1 text-center text-lg font-semibold text-gray-900">
                ${escapeHTML(formatCalendarMonthLabel(monthKey))}
            </h3>
            <div class="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div class="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                    ${WEEKDAY_LABELS.map((label) => `
                        <div class="px-2 py-2 text-center text-xs font-semibold text-gray-500">
                            ${label}
                        </div>
                    `).join("")}
                </div>
                <div class="grid grid-cols-7">
                    ${days.map((day) => {
                        const daySlots = entryLayout.slotsByDate.get(day.dateKey) || [];
                        const hiddenCount = entryLayout.hiddenCountByDate.get(day.dateKey) || 0;
                        const holiday = holidaysByDate.get(day.dateKey);
                        const isBusinessDay = activeDays.has(day.weekday);
                        const cellBackground = holiday ?
                            colors.holiday :
                            (!isBusinessDay ?
                                colors.nonBusinessDay :
                                (day.isCurrentMonth ? "#FFFFFF" : "#F3F4F6"));
                        const cellTextColor = getContrastTextColor(cellBackground);
                        const dayNumberStyle = day.dateKey === todayDateKey ?
                            "color: var(--dfc-brand-primary-contrast);" :
                            `color: ${cellTextColor};`;
                        const dayNumberClass = day.dateKey === todayDateKey ?
                            "relative inline-flex h-4 min-w-4 items-center justify-center text-xs font-bold leading-4" :
                            `text-xs font-semibold leading-4 ${day.isCurrentMonth ? "" : "opacity-50"}`;
                        const dayNumber = !day.isCurrentMonth && day.dayNumber === 1 ?
                            `${formatMonthAbbreviation(day.dateKey)} ${day.dayNumber}` :
                            day.dayNumber;
                        return `
                            <div
                                class="min-h-[6.5rem] border-b border-r border-gray-100 px-2 py-1.5"
                                data-calendar-date="${day.dateKey}"
                                ${day.isCurrentMonth ? "" : "data-calendar-adjacent-month=\"true\""}
                                style="background-color: ${cellBackground};"
                            >
                                <div class="flex items-center gap-1.5">
                                    <span
                                        class="shrink-0 ${dayNumberClass}"
                                        style="${dayNumberStyle}"
                                        data-calendar-day-number
                                        ${day.dateKey === todayDateKey ? "data-calendar-today-date" : ""}
                                    >
                                        ${day.dateKey === todayDateKey ? `
                                            <span
                                                class="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full shadow-sm"
                                                style="background-color: var(--dfc-brand-primary);"
                                                aria-hidden="true"
                                            ></span>
                                        ` : ""}
                                        <span class="relative z-10">
                                        ${escapeHTML(String(dayNumber))}
                                        </span>
                                    </span>
                                    ${holiday ? `
                                        <span
                                            class="flex min-h-4 min-w-0 flex-1 items-center whitespace-normal break-words rounded px-1 text-[10px] font-medium leading-[1.1]"
                                            style="color: ${cellTextColor};"
                                            title="${escapeHTML(holiday.name || "Holiday")}"
                                            data-calendar-holiday-label
                                        >
                                            ${escapeHTML(holiday.name || "Holiday")}
                                        </span>
                                    ` : ""}
                                </div>
                                <div class="mt-1 flex max-h-20 flex-col gap-0.5 overflow-visible">
                                    ${renderDayEntries(daySlots, colors, day)}
                                    ${renderDayOverflowButton(
                                        day,
                                        hiddenCount,
                                    )}
                                </div>
                            </div>
                        `;
                    }).join("")}
                </div>
            </div>
        </section>
    `;
}

function getMobileEntryInsetClasses(entry, day) {
    return shouldRenderEntryEdge(entry, day) ? "pl-1.5 pr-0.5" : "px-0.5";
}

function renderMobileDayEntries(slots, colors, day) {
    const adjacentMonthClass = day.isCurrentMonth ?
        "" :
        "calendar-entry-token--adjacent-month";
    return slots.map((slot) => {
        if (!slot?.entry) {
            return `<span class="calendar-mobile-entry-placeholder" aria-hidden="true"></span>`;
        }

        const entry = slot.entry;
        const entryColor = getEntryColor(entry, colors);
        const title = shouldShowSegmentTitle(entry, day) ?
            formatCalendarEntryTokenLabel(entry) :
            "";
        const edgeHtml = getEntryEdgeHtml(entry, day, entryColor);
        return `
            <span
                class="calendar-mobile-entry-token ${adjacentMonthClass} relative ${getMobileEntryInsetClasses(entry, day)} ${getEntryBarClasses(entry, day)}"
                style="background-color: ${entryColor}; color: ${getContrastTextColor(entryColor)};"
                title="${escapeHTML(formatCalendarEntryTooltip(entry))}"
            >
                ${edgeHtml}
                ${escapeHTML(title)}
            </span>
        `;
    }).join("");
}

function renderMobileDayOverflow(day, hiddenCount) {
    if (hiddenCount <= 0) {return "";}
    return `
        <span class="calendar-mobile-day-more">
            +${hiddenCount}
        </span>
    `;
}

function renderMobileMonthGrid(monthKey, calendarRouteState) {
    const colors = getCalendarColors();
    const { activeDays, holidaysByDate } = getBusinessCalendarMeta();
    const days = buildCalendarMonthDays(monthKey);
    const entryLayout = buildCalendarEntryLayout(
        days,
        calendarRouteState.entries,
        MAX_VISIBLE_DAY_ENTRIES,
    );
    const todayDateKey = getCurrentDateKey();

    return `
        <section class="calendar-mobile-month md:hidden" data-calendar-mobile-month-view>
            <div class="calendar-mobile-month-header">
                <button
                    type="button"
                    class="calendar-mobile-month-nav"
                    data-calendar-prev
                    aria-label="Previous month"
                    title="Previous month"
                >
                    <span class="material-symbols-outlined">chevron_left</span>
                </button>
                <h3 class="calendar-mobile-month-title">
                    ${escapeHTML(formatCalendarMonthLabel(monthKey))}
                </h3>
                <button
                    type="button"
                    class="calendar-mobile-month-nav"
                    data-calendar-next
                    aria-label="Next month"
                    title="Next month"
                >
                    <span class="material-symbols-outlined">chevron_right</span>
                </button>
            </div>
            <div class="calendar-mobile-month-shell">
                <div class="calendar-mobile-weekdays">
                    ${WEEKDAY_LABELS.map((label) => `
                        <div>${escapeHTML(label)}</div>
                    `).join("")}
                </div>
                <div class="calendar-mobile-month-grid">
                    ${days.map((day) => {
                        const daySlots = entryLayout.slotsByDate.get(day.dateKey) || [];
                        const hiddenCount = entryLayout.hiddenCountByDate.get(day.dateKey) || 0;
                        const holiday = holidaysByDate.get(day.dateKey);
                        const isBusinessDay = activeDays.has(day.weekday);
                        const cellBackground = holiday ?
                            colors.holiday :
                            (!isBusinessDay ?
                                colors.nonBusinessDay :
                                (day.isCurrentMonth ? "#FFFFFF" : "#F3F4F6"));
                        const isToday = day.dateKey === todayDateKey;
                        return `
                            <button
                                type="button"
                                class="calendar-mobile-day-cell ${day.isCurrentMonth ? "" : "calendar-mobile-day-cell--muted"}"
                                data-calendar-mobile-date="${escapeHTML(day.dateKey)}"
                                style="background-color: ${cellBackground};"
                                aria-label="${escapeHTML(formatDate(day.dateKey) || day.dateKey)}"
                            >
                                <span class="calendar-mobile-day-number ${isToday ? "calendar-mobile-day-number--today" : ""}">
                                    ${day.dayNumber}
                                </span>
                                <span class="calendar-mobile-entry-stack">
                                    ${renderMobileDayEntries(daySlots, colors, day)}
                                    ${renderMobileDayOverflow(day, hiddenCount)}
                                </span>
                            </button>
                        `;
                    }).join("")}
                </div>
            </div>
            ${renderMobileFloatingActions()}
        </section>
    `;
}

function renderMobileFloatingActions({ dateKey = "", agenda = false } = {}) {
    return `
        <div class="calendar-mobile-floating-actions">
            <button
                type="button"
                class="calendar-mobile-fab calendar-mobile-fab--secondary"
                data-calendar-mobile-today="${agenda ? "day" : "month"}"
                aria-label="Today"
                title="Today"
            >
                <span class="material-symbols-outlined">today</span>
            </button>
            <button
                type="button"
                class="calendar-mobile-fab"
                data-calendar-mobile-add
                ${dateKey ? `data-calendar-mobile-add-date="${escapeHTML(dateKey)}"` : ""}
                aria-label="Add Calendar entry"
                title="Add Calendar entry"
            >
                <span class="material-symbols-outlined">add</span>
            </button>
        </div>
    `;
}

function isReminderEntry(entry = {}) {
    return entry.renderKind === "reminder" || entry.sourceType === "reminder";
}

function isAllDayAgendaEntry(entry = {}) {
    return entry.sourceType === "leave" ||
        entry.renderKind === "leave" ||
        entry.isFullDay === true ||
        !entry.startTime;
}

function getEntryStartMinute(entry = {}) {
    const match = /^(\d{2}):(\d{2})$/.exec(String(entry.startTime || ""));
    if (!match) {return 0;}
    return (Number(match[1]) * 60) + Number(match[2]);
}

function getEntryDurationMinutes(entry = {}) {
    const start = getEntryStartMinute(entry);
    const match = /^(\d{2}):(\d{2})$/.exec(String(entry.endTime || ""));
    if (!match) {return 60;}
    const end = (Number(match[1]) * 60) + Number(match[2]);
    return Math.max(30, end - start);
}

function formatEntryDateRange(entry = {}) {
    if (!isMultiDayEntry(entry)) {return "";}
    const start = formatDate(entry.startDate) || entry.startDate || "";
    const endDate = entry.endDate || entry.startDate;
    const end = formatDate(endDate) || endDate || "";
    return start && end ? `${start} - ${end}` : "";
}

function getEntryMetaLabel(entry = {}) {
    const dateRange = formatEntryDateRange(entry);
    const time = formatEntryTime(entry);
    return [dateRange, time]
        .filter(Boolean)
        .join(" • ");
}

function renderMobileAgendaAllDayEntries(entries, colors) {
    if (!entries.length) {
        return `<p class="calendar-mobile-agenda-empty">No all day items.</p>`;
    }

    return sortAgendaEntries(entries).map((entry) => {
        const entryColor = getEntryColor(entry, colors);
        const isReminder = isReminderEntry(entry);
        const textColor = getContrastTextColor(entryColor);
        const dateRange = formatEntryDateRange(entry);
        return `
            <button
                type="button"
                class="calendar-mobile-all-day-entry ${isReminder ? "calendar-mobile-all-day-entry--reminder" : "calendar-mobile-all-day-entry--bar"}"
                data-calendar-mobile-agenda-entry
                data-calendar-agenda-kind="${isReminder ? "reminder" : "bar"}"
                data-calendar-agenda-source-type="${escapeHTML(entry.sourceType || "")}"
                data-calendar-agenda-source-id="${escapeHTML(entry.sourceId || "")}"
                style="${isReminder ?
                    "" :
                    `background-color: ${entryColor}; border-left-color: ${darkenHexColor(entryColor)}; color: ${textColor};`}"
            >
                ${isReminder ? `
                    <span class="calendar-mobile-entry-swatch" style="background-color: ${entryColor};"></span>
                ` : ""}
                <span class="min-w-0">
                    <span class="block truncate">${escapeHTML(entry.title || "Untitled")}</span>
                    ${dateRange ? `
                        <span class="block truncate text-[11px] opacity-80">${escapeHTML(dateRange)}</span>
                    ` : ""}
                </span>
            </button>
        `;
    }).join("");
}

function renderMobileTimedEntry(entry, colors) {
    const entryColor = getEntryColor(entry, colors);
    const isReminder = isReminderEntry(entry);
    const top = MOBILE_TIMELINE_TOP_OFFSET +
        ((getEntryStartMinute(entry) / 60) * MOBILE_TIMELINE_HOUR_HEIGHT);
    const height = Math.max(
        isReminder ? 24 : 30,
        (getEntryDurationMinutes(entry) / 60) * MOBILE_TIMELINE_HOUR_HEIGHT,
    );

    return `
        <button
            type="button"
            class="calendar-mobile-timed-entry ${isReminder ? "calendar-mobile-timed-entry--reminder" : "calendar-mobile-timed-entry--bar"}"
            data-calendar-mobile-agenda-entry
            data-calendar-agenda-kind="${isReminder ? "reminder" : "bar"}"
            data-calendar-agenda-source-type="${escapeHTML(entry.sourceType || "")}"
            data-calendar-agenda-source-id="${escapeHTML(entry.sourceId || "")}"
            style="top: ${top}px; height: ${height}px; ${isReminder ?
                "" :
                `background-color: ${entryColor}; border-left-color: ${darkenHexColor(entryColor)}; color: ${getContrastTextColor(entryColor)};`}"
        >
            ${isReminder ? `
                <span class="calendar-mobile-entry-swatch" style="background-color: ${entryColor};"></span>
            ` : ""}
            <span class="calendar-mobile-timed-entry-copy">
                <span class="calendar-mobile-timed-entry-title">${escapeHTML(entry.title || "Untitled")}</span>
                <span class="calendar-mobile-timed-entry-time">${escapeHTML(getEntryMetaLabel(entry))}</span>
            </span>
        </button>
    `;
}

function renderMobileTimeGrid(entries, colors) {
    const timelineHeight = MOBILE_TIMELINE_TOP_OFFSET + (24 * MOBILE_TIMELINE_HOUR_HEIGHT);
    return `
        <div class="calendar-mobile-time-grid" style="height: ${timelineHeight}px;">
            <div class="calendar-mobile-time-labels">
                ${Array.from({ length: 24 }, (_, hour) => `
                    <div
                        class="calendar-mobile-time-label ${hour === 0 ? "calendar-mobile-time-label--first" : ""}"
                        style="top: ${MOBILE_TIMELINE_TOP_OFFSET + (hour * MOBILE_TIMELINE_HOUR_HEIGHT)}px;"
                    >
                        ${hour === 0 ? "12 am" : hour < 12 ? `${hour} am` : hour === 12 ? "12 pm" : `${hour - 12} pm`}
                    </div>
                `).join("")}
            </div>
            <div class="calendar-mobile-time-lane">
                ${Array.from({ length: 24 }, (_, hour) => `
                    <div
                        class="calendar-mobile-hour-line"
                        style="top: ${MOBILE_TIMELINE_TOP_OFFSET + (hour * MOBILE_TIMELINE_HOUR_HEIGHT)}px;"
                    ></div>
                `).join("")}
                ${sortAgendaEntries(entries).map((entry) => renderMobileTimedEntry(entry, colors)).join("")}
            </div>
        </div>
    `;
}

function renderMobileWeekStrip(selectedDateKey) {
    const todayDateKey = getCurrentDateKey();
    const selectedWeekStart = getCalendarWeekStart(selectedDateKey);
    const startDateKey = addCalendarDays(
        selectedWeekStart,
        -Math.floor(MOBILE_DATE_STRIP_WINDOW_DAYS / 2),
    );
    const days = Array.from({ length: MOBILE_DATE_STRIP_WINDOW_DAYS }, (_, index) => {
        return addCalendarDays(startDateKey, index);
    }).filter(Boolean);

    return `
        <div
            class="calendar-mobile-week-strip"
            data-calendar-mobile-week-strip
            data-calendar-selected-date="${escapeHTML(selectedDateKey)}"
        >
            ${days.map((dateKey) => renderMobileWeekStripDay({
                dateKey,
                selectedDateKey,
                todayDateKey,
            })).join("")}
        </div>
    `;
}

function renderMobileWeekStripDay({ dateKey, selectedDateKey, todayDateKey }) {
    const date = parseDateKey(dateKey);
    const isToday = dateKey === todayDateKey;
    const isSelected = dateKey === selectedDateKey;
    const isMonthStart = date?.getDate() === 1;
    const dayNumber = date ? date.getDate() : "";
    return `
        <button
            type="button"
            class="calendar-mobile-week-day ${getMonthBandClass(dateKey)} ${isSelected ? "is-selected" : ""} ${isToday ? "is-today" : ""}"
            data-calendar-mobile-agenda-date="${escapeHTML(dateKey)}"
            data-calendar-month-key="${escapeHTML(dateKey.slice(0, 7))}"
            ${isMonthStart ? "data-calendar-month-start" : ""}
        >
            <span class="calendar-mobile-weekday-label">${escapeHTML(formatWeekdayLabel(dateKey))}</span>
            <span
                class="${isMonthStart ?
                    "calendar-mobile-weekday-month" :
                    "calendar-mobile-weekday-month calendar-mobile-weekday-month--empty"}"
                ${isMonthStart ? "" : "aria-hidden=\"true\""}
            >
                ${isMonthStart ? escapeHTML(formatMonthAbbreviation(dateKey)) : ""}
            </span>
            <span class="calendar-mobile-weekday-number">
                <span>${escapeHTML(String(dayNumber))}</span>
            </span>
        </button>
    `;
}

function renderMobileDayAgenda(dateKey, calendarRouteState) {
    const colors = getCalendarColors();
    const entries = sortAgendaEntries(getEntriesForDate(calendarRouteState.entries, dateKey));
    const allDayEntries = entries.filter(isAllDayAgendaEntry);
    const timedEntries = entries.filter((entry) => !isAllDayAgendaEntry(entry));

    return `
        <section class="calendar-mobile-agenda md:hidden" data-calendar-mobile-agenda-view>
            <div class="calendar-mobile-agenda-heading">
                <h3>Agenda</h3>
                <p>${escapeHTML(formatDate(dateKey) || dateKey)}</p>
            </div>
            ${renderMobileWeekStrip(dateKey)}
            ${allDayEntries.length ? `
                <div class="calendar-mobile-all-day-section">
                    <h4>All Day</h4>
                    <div class="calendar-mobile-all-day-list">
                        ${renderMobileAgendaAllDayEntries(allDayEntries, colors)}
                    </div>
                </div>
            ` : ""}
            ${renderMobileTimeGrid(timedEntries, colors)}
            ${renderMobileFloatingActions({ dateKey, agenda: true })}
        </section>
    `;
}

function shouldOpenAddEntryFromDateClick(event) {
    if (event.target.closest(
        ".calendar-entry-token, [data-calendar-day-agenda], button, a, input, select, textarea",
    )) {
        return false;
    }
    return true;
}

function bindCalendarControls(container) {
    container.querySelectorAll("[data-calendar-prev]").forEach((button) => {
        button.addEventListener("click", () => {
            pendingNavigationDirection = "previous";
            moveCalendarVisibleMonth(-1, { onData: renderCalendarView });
        });
    });
    container.querySelectorAll("[data-calendar-next]").forEach((button) => {
        button.addEventListener("click", () => {
            pendingNavigationDirection = "next";
            moveCalendarVisibleMonth(1, { onData: renderCalendarView });
        });
    });
    container.querySelectorAll("[data-calendar-today]").forEach((button) => {
        button.addEventListener("click", () => {
            showCurrentCalendarMonth({ onData: renderCalendarView });
        });
    });
    container.querySelector("[data-calendar-add-entry]")?.addEventListener("click", () => {
        openCalendarAddEntryModal();
    });
    container.querySelectorAll("[data-calendar-mobile-date]").forEach((dayCell) => {
        dayCell.addEventListener("click", () => {
            navigate("calendar", `day/${dayCell.dataset.calendarMobileDate}`);
        });
    });
    bindMobileAgendaDateButtons(container);
    container.querySelectorAll("[data-calendar-mobile-add]").forEach((button) => {
        button.addEventListener("click", () => {
            const dateKey = button.dataset.calendarMobileAddDate;
            openCalendarAddEntryModal(dateKey ? { dateKey } : {});
        });
    });
    container.querySelectorAll("[data-calendar-mobile-today]").forEach((button) => {
        button.addEventListener("click", () => {
            if (button.dataset.calendarMobileToday === "day") {
                navigate("calendar", `day/${getCurrentDateKey()}`);
                return;
            }
            showCurrentCalendarMonth({ onData: renderCalendarView });
            if (state.calendarView) {
                navigate("calendar", null);
            }
        });
    });
    container.querySelectorAll("[data-calendar-mobile-agenda-entry]").forEach((button) => {
        button.addEventListener("click", () => {
            openAgendaEntry({
                sourceType: button.dataset.calendarAgendaSourceType,
                sourceId: button.dataset.calendarAgendaSourceId,
                title: button.textContent?.trim() || "",
            });
        });
    });
    container.querySelectorAll("[data-calendar-date]").forEach((dayCell) => {
        dayCell.addEventListener("click", (event) => {
            if (!shouldOpenAddEntryFromDateClick(event)) {return;}
            openCalendarAddEntryModal({ dateKey: dayCell.dataset.calendarDate });
        });
    });
    container.querySelectorAll("[data-calendar-day-agenda]").forEach((button) => {
        button.addEventListener("click", (event) => {
            event.stopPropagation();
            const dateKey = button.dataset.calendarDayAgenda;
            const entries = getEntriesForDate(getCalendarState().entries, dateKey);
            openCalendarDayAgenda(dateKey, entries);
        });
    });
    container.querySelectorAll(".calendar-entry-token").forEach((button) => {
        button.addEventListener("dblclick", (event) => {
            event.stopPropagation();
        });
        button.addEventListener("click", () => {
            if (button.dataset.calendarSourceType === "event") {
                openEventDetailModal({
                    sourceType: button.dataset.calendarSourceType,
                    sourceId: button.dataset.calendarSourceId,
                    title: button.textContent?.trim() || "",
                });
            } else if (button.dataset.calendarSourceType === "reminder") {
                openReminderDetailModal({
                    sourceType: button.dataset.calendarSourceType,
                    sourceId: button.dataset.calendarSourceId,
                    title: button.textContent?.trim() || "",
                });
            } else if (button.dataset.calendarSourceType === "leave") {
                openLeaveDetailModal({
                    sourceType: button.dataset.calendarSourceType,
                    sourceId: button.dataset.calendarSourceId,
                    title: button.textContent?.trim() || "",
                });
            }
        });
    });
}

function bindMobileAgendaDateButtons(container) {
    container.querySelectorAll("[data-calendar-mobile-agenda-date]").forEach((dayButton) => {
        if (dayButton.dataset.calendarAgendaDateBound === "true") {return;}
        dayButton.dataset.calendarAgendaDateBound = "true";
        dayButton.addEventListener("click", () => {
            navigate("calendar", `day/${dayButton.dataset.calendarMobileAgendaDate}`);
        });
    });
}

function extendMobileWeekStrip(strip, direction) {
    const selectedDateKey = strip.dataset.calendarSelectedDate || getCurrentDateKey();
    const todayDateKey = getCurrentDateKey();
    const dateButtons = Array.from(strip.querySelectorAll("[data-calendar-mobile-agenda-date]"));
    const firstDateKey = dateButtons[0]?.dataset.calendarMobileAgendaDate;
    const lastDateKey = dateButtons[dateButtons.length - 1]?.dataset.calendarMobileAgendaDate;
    if (!firstDateKey || !lastDateKey) {return;}

    if (direction === "previous") {
        const previousDays = Array.from({ length: MOBILE_DATE_STRIP_WINDOW_DAYS }, (_, index) => {
            return addCalendarDays(firstDateKey, index - MOBILE_DATE_STRIP_WINDOW_DAYS);
        }).filter(Boolean);
        const previousWidth = strip.scrollWidth;
        strip.insertAdjacentHTML("afterbegin", previousDays.map((dateKey) => {
            return renderMobileWeekStripDay({ dateKey, selectedDateKey, todayDateKey });
        }).join(""));
        bindMobileAgendaDateButtons(strip);
        strip.scrollLeft += strip.scrollWidth - previousWidth;
        return;
    }

    const nextDays = Array.from({ length: MOBILE_DATE_STRIP_WINDOW_DAYS }, (_, index) => {
        return addCalendarDays(lastDateKey, index + 1);
    }).filter(Boolean);
    strip.insertAdjacentHTML("beforeend", nextDays.map((dateKey) => {
        return renderMobileWeekStripDay({ dateKey, selectedDateKey, todayDateKey });
    }).join(""));
    bindMobileAgendaDateButtons(strip);
}

function bindMobileWeekStripScroll(container) {
    const strip = container.querySelector("[data-calendar-mobile-week-strip]");
    if (!strip) {return;}
    strip.addEventListener("scroll", () => {
        if (strip.scrollLeft < MOBILE_DATE_STRIP_EDGE_THRESHOLD) {
            extendMobileWeekStrip(strip, "previous");
            return;
        }
        if (
            strip.scrollLeft + strip.clientWidth >
            strip.scrollWidth - MOBILE_DATE_STRIP_EDGE_THRESHOLD
        ) {
            extendMobileWeekStrip(strip, "next");
        }
    }, { passive: true });
}

function alignMobileWeekStrip(container) {
    const selectedDay = container.querySelector(
        "[data-calendar-mobile-week-strip] .calendar-mobile-week-day.is-selected",
    );
    selectedDay?.scrollIntoView({
        block: "nearest",
        inline: "center",
    });
}

function formatEntryTime(entry) {
    const time = [entry.startTime, entry.endTime]
        .filter(Boolean)
        .join(" - ");
    return time || (entry.isFullDay ? "All day" : "");
}

function renderAgendaEntries(dateKey, entries, colors) {
    if (!entries.length) {
        return `<p class="text-sm text-gray-500">No items for ${escapeHTML(formatDate(dateKey))}.</p>`;
    }

    const sortedEntries = sortAgendaEntries(entries);
    return `
        <div class="space-y-2" data-calendar-day-agenda-list>
            ${sortedEntries.map((entry) => {
                const entryColor = getEntryColor(entry, colors);
                const title = entry.title || "Untitled";
                return `
                    <button
                        type="button"
                        class="flex w-full items-start gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-left transition-colors hover:border-[var(--dfc-brand-primary)]"
                        data-calendar-agenda-source-type="${escapeHTML(entry.sourceType || "")}"
                        data-calendar-agenda-source-id="${escapeHTML(entry.sourceId || "")}"
                    >
                        <span
                            class="mt-1 h-3 w-3 shrink-0 rounded-full border border-black/5"
                            style="background-color: ${entryColor};"
                            aria-hidden="true"
                        ></span>
                        <span class="min-w-0">
                            <span class="block truncate text-sm font-semibold text-gray-900">
                                ${escapeHTML(title)}
                            </span>
                            <span class="block text-xs text-gray-500">
                                ${escapeHTML(getEntryMetaLabel(entry))}
                            </span>
                        </span>
                    </button>
                `;
            }).join("")}
        </div>
    `;
}

function openAgendaEntry(entry) {
    if (entry.sourceType === "event") {
        openEventDetailModal(entry);
    } else if (entry.sourceType === "reminder") {
        openReminderDetailModal(entry);
    } else if (entry.sourceType === "leave") {
        openLeaveDetailModal(entry);
    }
}

function openCalendarDayAgenda(dateKey, entries = []) {
    const colors = getCalendarColors();
    openStandardForm("calendar_day_agenda", null, {
        getFieldsHtml: () => renderAgendaEntries(dateKey, entries, colors),
        afterRender({ formFields, itemForm }) {
            const formTitle = document.querySelector("#form-title");
            const cancelButton = document.getElementById("cancel-form-btn");
            const submitButton = itemForm.querySelector("button[type=\"submit\"]");

            if (formTitle) {
                formTitle.textContent = `Agenda - ${formatDate(dateKey) || dateKey}`;
            }
            if (submitButton) {
                submitButton.style.display = "none";
            }
            if (cancelButton) {
                cancelButton.textContent = "Close";
            }
            itemForm.onsubmit = (event) => event.preventDefault();
            formFields
                .querySelectorAll("[data-calendar-agenda-source-id]")
                .forEach((button) => {
                    button.addEventListener("click", () => {
                        openAgendaEntry({
                            sourceType: button.dataset.calendarAgendaSourceType,
                            sourceId: button.dataset.calendarAgendaSourceId,
                            title: button.textContent?.trim() || "",
                        });
                    });
                });
        },
    });
}

function renderCalendarMessages(calendarRouteState) {
    const overflowMonthKeys = Array.isArray(calendarRouteState.overflowMonthKeys) ?
        calendarRouteState.overflowMonthKeys :
        [];
    const overflowMessage = overflowMonthKeys.length > 0 ?
        `Some entries are not shown for ${overflowMonthKeys
            .map((monthKey) => formatCalendarMonthLabel(monthKey))
            .join(", ")} because the month has too many calendar entries.` :
        "";
    const partialLoadMessage = calendarRouteState.listenerStatus === "partial" &&
        calendarRouteState.listenerError ?
        "Some calendar months could not be loaded. The visible entries may be incomplete." :
        "";
    return `
        ${overflowMessage ? `
            <div class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                ${escapeHTML(overflowMessage)}
            </div>
        ` : ""}
        ${partialLoadMessage ? `
            <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                ${escapeHTML(partialLoadMessage)}
            </div>
        ` : ""}
    `;
}

function renderDesktopCalendarBody(calendarRouteState) {
    const startMonth = calendarRouteState.visibleStartMonth;
    const secondMonth = addCalendarMonths(startMonth, 1);
    const todayNavigationTarget = getTodayNavigationTarget(calendarRouteState);
    const navigationDirection = pendingNavigationDirection &&
        lastRenderedStartMonth &&
        startMonth !== lastRenderedStartMonth ?
        pendingNavigationDirection :
        "";
    lastRenderedStartMonth = startMonth;
    pendingNavigationDirection = "";
    return `
        <div class="relative" data-calendar-surface>
            <div class="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between">
                <div class="flex items-center gap-1">
                    <button
                        type="button"
                        class="header-action-btn pointer-events-auto text-gray-500 transition-colors"
                        data-calendar-prev
                        aria-label="Previous month"
                        title="Previous month"
                    >
                        <span class="material-symbols-outlined text-2xl">chevron_left</span>
                    </button>
                    ${todayNavigationTarget === "previous" ? `
                        <button
                            type="button"
                            class="header-action-btn pointer-events-auto text-gray-500 transition-colors"
                            data-calendar-today
                            aria-label="Show current month"
                            title="Show current month"
                        >
                            <span class="material-symbols-outlined text-[21px]">today</span>
                        </button>
                    ` : ""}
                </div>
                <div class="flex items-center gap-1">
                    ${todayNavigationTarget === "next" ? `
                        <button
                            type="button"
                            class="header-action-btn pointer-events-auto text-gray-500 transition-colors"
                            data-calendar-today
                            aria-label="Show current month"
                            title="Show current month"
                        >
                            <span class="material-symbols-outlined text-[21px]">today</span>
                        </button>
                    ` : ""}
                    <button
                        type="button"
                        class="header-action-btn pointer-events-auto text-gray-500 transition-colors"
                        data-calendar-next
                        aria-label="Next month"
                        title="Next month"
                    >
                        <span class="material-symbols-outlined text-2xl">chevron_right</span>
                    </button>
                </div>
            </div>
            <div
                class="calendar-month-grid grid gap-4 xl:grid-cols-2"
                data-calendar-loaded-months="${escapeHTML(calendarRouteState.loadedMonthKeys.join(","))}"
                ${navigationDirection ? `data-calendar-nav-direction="${navigationDirection}"` : ""}
            >
                ${renderMonthGrid(startMonth, calendarRouteState)}
                ${secondMonth ? renderMonthGrid(secondMonth, calendarRouteState, {
                    secondary: true,
                }) : ""}
            </div>
        </div>
    `;
}

function isMobileCalendarViewport() {
    return window.matchMedia("(max-width: 767px)").matches;
}

function renderMobileCalendarBody(calendarRouteState) {
    const agendaDateKey = getCalendarDayDateKey(state.calendarView);
    if (agendaDateKey) {
        return renderMobileDayAgenda(agendaDateKey, calendarRouteState);
    }
    return renderMobileMonthGrid(calendarRouteState.visibleStartMonth, calendarRouteState);
}

function renderCalendarBody(calendarRouteState) {
    if (calendarRouteState.listenerStatus === "loading") {
        return `
            <div class="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
                Loading shared calendar entries...
            </div>
        `;
    }

    if (calendarRouteState.listenerStatus === "error") {
        return `
            <div class="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                Shared Calendar entries could not be loaded. Try refreshing the page.
            </div>
        `;
    }

    return `
        ${renderCalendarMessages(calendarRouteState)}
        <div class="hidden md:block">
            ${renderDesktopCalendarBody(calendarRouteState)}
        </div>
        <div class="md:hidden">
            ${renderMobileCalendarBody(calendarRouteState)}
        </div>
    `;
}

/**
 * Renders the route-backed Shared Calendar shell.
 * @returns {void}
 */
export function renderCalendarView() {
    const container = document.getElementById("calendar-board");
    if (!container) {
        console.error("Shared Calendar container (#calendar-board) not found.");
        return;
    }

    if (!canAccessView(state.currentUserProfile, "calendar")) {
        detachCalendarRouteListeners();
        container.innerHTML = `
            <section class="flex w-full flex-col gap-4">
                <div>
                    <h2 class="text-2xl font-semibold text-gray-900">Calendar</h2>
                </div>
                <div class="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
                    You do not have permission to view the Shared Calendar.
                </div>
            </section>
        `;
        return;
    }

    const agendaDateKey = getCalendarDayDateKey(state.calendarView);
    if (agendaDateKey && isMobileCalendarViewport()) {
        showCalendarMonth(agendaDateKey.slice(0, 7), { onData: renderCalendarView });
    } else {
        attachCalendarRouteListeners({ onData: renderCalendarView });
    }

    const calendarRouteState = getCalendarState();
    container.innerHTML = `
        <section class="flex w-full flex-col gap-3">
            ${renderCalendarBody(calendarRouteState)}
        </section>
    `;
    bindCalendarControls(container);
    bindMobileWeekStripScroll(container);
    alignMobileWeekStrip(container);
}
